import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { observer } from 'mobx-react-lite'
import {
  Box, Button, Chip, CircularProgress, Divider, IconButton, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Tooltip, Typography,
} from '@mui/material'
import {
  Label as TagIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowForward as NextIcon,
} from '@mui/icons-material'
import { toast } from 'react-toastify'
import { tagsApi, Tag } from '../lib/api/leadsApi'
import { orgsApi, Org } from '../lib/api/tenantsApi'
import { PageHeader } from '../components/shared/PageHeader'
import { PageActionButtons } from '../components/shared/PageActionButtons'
import { DeleteDialog } from '../components/shared/DeleteDialog'
import { themeColors } from '../app/layout/theme'
import { authStore } from '../lib/stores/authStore'
import { FormInput, FormSelect, FormDialog } from '../components/ui'
import { usePermission } from '../lib/hooks/usePermission'
import { ConfirmDialog } from '../components/shared/ConfirmDialog'
import { useOrgFilter, useOrgSelector } from '../context/OrgSelectorContext'

// ── Preset colour palette ─────────────────────────────────────────────────────

const PRESET_COLORS = [
  '#ef4444', '#f97316', '#eab308', '#16a34a',
  '#0ea5e9', '#3b82f6', '#7c3aed', '#ec4899',
  '#14b8a6', '#64748b', '#dc2626', '#059669',
]

function ColorPicker({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  return (
    <Box>
      <Typography fontSize="12px" color="text.secondary" fontWeight={500} mb={1}>Colour</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {PRESET_COLORS.map(c => (
          <Box key={c} onClick={() => onChange(c)} sx={{
            width: 28, height: 28, borderRadius: '50%', bgcolor: c,
            cursor: 'pointer', border: value === c ? `3px solid #1d4ed8` : '2px solid transparent',
            outline: value === c ? `2px solid #fff` : 'none',
            boxShadow: value === c ? `0 0 0 3px ${c}60` : 'none',
            transition: 'all 0.15s',
            '&:hover': { transform: 'scale(1.18)' },
          }} />
        ))}
      </Box>
    </Box>
  )
}

// ── SuperAdmin: org picker step ───────────────────────────────────────────────

function OrgPickerDialog({ open, orgs, onClose, onSelect }: {
  open: boolean
  orgs: Org[]
  onClose: () => void
  onSelect: (orgId: string | null, orgName: string) => void
}) {
  const [selectedOrgId, setSelectedOrgId] = useState('ALL')

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSave={() => {
        if (selectedOrgId === 'ALL') {
          onSelect(null, 'All Organizations')
          return
        }
        const org = orgs.find(o => o.id === selectedOrgId)
        if (org) onSelect(org.id, org.name)
      }}
      title="Select Organisation"
      saveLabel="Next"
      maxWidth="xs"
    >
      <Typography fontSize="12.5px" color="text.secondary" mb={2}>
        Tags are scoped to an organisation. Select a specific org or create a tag for all organisations.
      </Typography>
      <FormSelect
        label="Organisation"
        required
        value={selectedOrgId}
        onChange={v => setSelectedOrgId((v?.value ?? v) as string)}
        options={[
          { label: 'All Organizations', value: 'ALL' },
          ...orgs.map(o => ({ label: o.name, value: o.id })),
        ]}
        fullWidth
      />
    </FormDialog>
  )
}

// ── Tag Form Dialog ───────────────────────────────────────────────────────────

function TagFormDialog({ open, tag, orgName, onClose, onSave, isSaving }: {
  open: boolean
  tag?: Tag | null
  orgName?: string
  onClose: () => void
  onSave: (name: string, color: string) => Promise<void>
  isSaving: boolean
}) {
  const isEdit = !!tag
  const [name,  setName]  = useState('')
  const [color, setColor] = useState(PRESET_COLORS[0]!)
  const [err,   setErr]   = useState('')
  const [isSaveConfirmOpen, setIsSaveConfirmOpen] = useState(false)

  useMemo(() => {
    if (open) {
      setName(tag?.name ?? '')
      setColor(tag?.color ?? PRESET_COLORS[0]!)
      setErr('')
      setIsSaveConfirmOpen(false)
    }
  }, [open, tag])

  const handleSave = async () => {
    if (!name.trim()) { setErr('Tag name is required'); return }
    if (isEdit) {
      setIsSaveConfirmOpen(true)
    } else {
      await onSave(name.trim(), color)
    }
  }

  const handleConfirmSave = async () => {
    setIsSaveConfirmOpen(false)
    await onSave(name.trim(), color)
  }

  return (
    <>
      <FormDialog
        open={open}
        onClose={onClose}
        onSave={handleSave}
        title={isEdit ? 'Edit Tag' : 'New Tag'}
        saveLabel={isEdit ? 'Save Changes' : 'Create Tag'}
        saving={isSaving}
        maxWidth="xs"
      >
        <Stack spacing={2.5}>
          {orgName && (
            <Typography fontSize="12px" color="text.secondary" sx={{ mt: -1 }}>
              Organisation: <strong>{orgName}</strong>
            </Typography>
          )}
          {/* Preview */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography fontSize="12px" color="text.secondary" fontWeight={500}>Preview:</Typography>
            <Chip
              label={name || 'Tag name'}
              size="small"
              sx={{
                bgcolor: `${color}20`, color, border: `1px solid ${color}50`,
                fontWeight: 700, fontSize: '12px', height: 24,
              }}
            />
          </Box>

          <FormInput
            label="Tag Name"
            required
            value={name}
            onChange={e => { setName(e.target.value); setErr('') }}
            error={!!err}
            helperText={err || 'e.g. Hot Lead, Cold Lead, High Value'}
            fullWidth
            placeholder="Enter tag name"
          />

          <ColorPicker value={color} onChange={setColor} />
        </Stack>
      </FormDialog>

      <ConfirmDialog
        open={isSaveConfirmOpen}
        title="Save Changes"
        description="Are you sure you want to save the changes to this tag?"
        confirmLabel="Save"
        confirmColor="primary"
        onClose={() => setIsSaveConfirmOpen(false)}
        onConfirm={handleConfirmSave}
      />
    </>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

const AVAILABLE_TAGS_TEMPLATES = [
  { name: 'Cold Lead', color: '#0ea5e9' },
  { name: 'Warm Lead', color: '#f97316' },
  { name: 'Hot Lead', color: '#ef4444' },
]

const TagsPage = observer(function TagsPage() {
  const qc = useQueryClient()

  const { hasAction, isSuperAdmin } = usePermission()
  const canCreate = isSuperAdmin || hasAction('/settings', 'create_tag') || hasAction('/settings', 'update')
  const canUpdate = isSuperAdmin || hasAction('/settings', 'update') 
  const canDelete = isSuperAdmin || hasAction('/settings', 'delete')

  const orgFilter = useOrgFilter()
  const { selectedOrgId: globalSelectedOrgId, selectedOrgName: globalSelectedOrgName } = useOrgSelector()

  // Dialog state
  const [orgPickerOpen,  setOrgPickerOpen]  = useState(false)
  const [formOpen,       setFormOpen]       = useState(false)
  const [editingTag,     setEditingTag]     = useState<Tag | null>(null)
  const [deletingTag,    setDeletingTag]    = useState<Tag | null>(null)
  // SuperAdmin flow: org selected before creating a tag
  const [selectedOrgId,   setSelectedOrgId]   = useState<string | null>(null)
  const [selectedOrgName, setSelectedOrgName] = useState<string | null>(null)

  // Templates selection
  const [selectedTemplates, setSelectedTemplates] = useState<string[]>([])
  const [assigningTemplates, setAssigningTemplates] = useState(false)

  const { data: tags = [], isLoading } = useQuery({
    queryKey: ['tags', orgFilter],
    queryFn:  () => tagsApi.getAll(orgFilter),
    staleTime: 30_000,
  })

  // Only fetched when SuperAdmin and canCreate
  const { data: orgs = [] } = useQuery({
    queryKey: ['orgs'],
    queryFn:  orgsApi.getAll,
    enabled:  isSuperAdmin && canCreate,
    staleTime: 60_000,
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['tags'] })

  const createMut = useMutation({
    mutationFn: ({ name, color }: { name: string; color: string }) =>
      tagsApi.create({
        name,
        color,
        ...(isSuperAdmin ? { orgId: selectedOrgId || globalSelectedOrgId || undefined } : {}),
      }),
    onSuccess: (data) => {
      invalidate()
      setFormOpen(false)
      setSelectedOrgId(null)
      setSelectedOrgName(null)

      if (data.isBulk) {
        const createdList = data.createdOrgs.length > 0
          ? `Created:\n${data.createdOrgs.map(o => `- ${o}`).join('\n')}`
          : 'None created'
        const skippedList = data.skippedOrgs.length > 0
          ? `Skipped (already exists):\n${data.skippedOrgs.map(o => `- ${o}`).join('\n')}`
          : ''

        toast.info(
          <div style={{ whiteSpace: 'pre-wrap' }}>
            <strong>Tag creation completed.</strong>
            <div style={{ marginTop: '8px' }}>{createdList}</div>
            {skippedList && <div style={{ marginTop: '8px' }}>{skippedList}</div>}
          </div>,
          { autoClose: 8000 }
        )
      } else {
        toast.success('Tag created successfully')
      }
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to create tag'),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, name, color }: { id: string; name: string; color: string }) =>
      tagsApi.update(id, { name, color }),
    onSuccess: () => { invalidate(); setEditingTag(null); toast.success('Tag updated') },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to update tag'),
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => tagsApi.delete(id),
    onSuccess: () => { invalidate(); setDeletingTag(null); toast.success('Tag deleted') },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to delete tag'),
  })

  const handleNewTagClick = () => {
    if (isSuperAdmin && !globalSelectedOrgId) {
      setOrgPickerOpen(true)
    } else {
      if (globalSelectedOrgId) {
        setSelectedOrgId(globalSelectedOrgId)
        setSelectedOrgName(globalSelectedOrgName)
      } else {
        setSelectedOrgId(null)
        setSelectedOrgName(null)
      }
      setFormOpen(true)
    }
  }

  const handleOrgSelected = async (orgId: string | null, orgName: string) => {
    setOrgPickerOpen(false)
    if (assigningTemplates) {
      setAssigningTemplates(false)
      try {
        const templatesToAdd = AVAILABLE_TAGS_TEMPLATES.filter(t => selectedTemplates.includes(t.name))
        await Promise.all(
          templatesToAdd.map(t =>
            tagsApi.create({
              name: t.name,
              color: t.color,
              ...(orgId ? { orgId } : {})
            })
          )
        )
        setSelectedTemplates([])
        invalidate()
        toast.success(`Selected tags added to ${orgName}`)
      } catch (err: any) {
        toast.error(err?.response?.data?.message ?? 'Failed to assign tags')
      }
    } else {
      setSelectedOrgId(orgId)
      setSelectedOrgName(orgName)
      setFormOpen(true)
    }
  }

  const handleAssignSelectedTags = async () => {
    if (isSuperAdmin && !globalSelectedOrgId) {
      setAssigningTemplates(true)
      setOrgPickerOpen(true)
      return
    }

    try {
      const templatesToAdd = AVAILABLE_TAGS_TEMPLATES.filter(t => selectedTemplates.includes(t.name))
      const targetOrgId = globalSelectedOrgId || undefined
      await Promise.all(
        templatesToAdd.map(t =>
          tagsApi.create({
            name: t.name,
            color: t.color,
            ...(targetOrgId ? { orgId: targetOrgId } : {})
          })
        )
      )
      setSelectedTemplates([])
      invalidate()
      toast.success('Selected tags added to organization')
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? 'Failed to assign tags')
    }
  }

  const formatDate = (iso: string) => {
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        title="Tags"
        breadcrumb={[{ label: 'Settings', to: '/settings' }]}
        showBreadcrumb
        actions={canCreate ? (
          <PageActionButtons
            onNew={handleNewTagClick}
            newLabel="New Tag"
          />
        ) : undefined}
      />

      <Box sx={{ flex: 1, p: 3, overflowY: 'auto' }}>

        <Box sx={{ mb: 2 }}>
          <Typography fontSize="13px" color="text.secondary">
            Tags help you organise and prioritise leads. Add them to any lead from the lead detail page.
          </Typography>
        </Box>

        {tags.length === 0 && !isLoading && (
          <Box sx={{ mb: 4, p: 3, bgcolor: '#eff6ff', border: '1px solid #dbeafe', borderRadius: '12px' }}>
            <Typography fontSize="14px" fontWeight={600} color="#1e40af" mb={1}>
              Assign Existing Tags to Organization
            </Typography>
            <Typography fontSize="13px" color="#3b82f6" mb={2.5}>
              This organization currently has no tags. Select the tags you want to assign below and click "Add Selected Tags".
            </Typography>
            <Stack spacing={1.5} sx={{ mb: 3 }}>
              {AVAILABLE_TAGS_TEMPLATES.map(t => {
                const isSelected = selectedTemplates.includes(t.name)
                return (
                  <Box
                    key={t.name}
                    onClick={() => {
                      setSelectedTemplates(prev =>
                        prev.includes(t.name)
                          ? prev.filter(x => x !== t.name)
                          : [...prev, t.name]
                      )
                    }}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 2, p: 1.5,
                      borderRadius: '10px', border: `1.5px solid ${isSelected ? themeColors.primary : '#dbeafe'}`,
                      bgcolor: isSelected ? `${themeColors.primary}08` : '#fff',
                      cursor: 'pointer', transition: 'all 0.15s',
                      '&:hover': { borderColor: themeColors.primary },
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      readOnly
                      style={{ cursor: 'pointer', width: 16, height: 16 }}
                    />
                    <Chip
                      label={t.name}
                      size="small"
                      sx={{
                        bgcolor: `${t.color}20`, color: t.color, border: `1px solid ${t.color}50`,
                        fontWeight: 700, fontSize: '12.5px', height: 24,
                      }}
                    />
                    <Typography fontSize="12.5px" color="text.secondary">
                      {t.name.startsWith('Hot') ? 'High priority, urgent action needed' :
                       t.name.startsWith('Warm') ? 'Active interest shown, follow up needed' :
                       'Low priority, keep in touch'}
                    </Typography>
                  </Box>
                )
              })}
            </Stack>
            <Button
              variant="contained"
              onClick={handleAssignSelectedTags}
              disabled={selectedTemplates.length === 0}
              sx={{
                borderRadius: '8px', textTransform: 'none', fontWeight: 600,
                backgroundColor: themeColors.primary,
                '&:hover': { backgroundColor: themeColors.primaryDark }
              }}
            >
              Add Selected Tags ({selectedTemplates.length})
            </Button>
          </Box>
        )}

        <TableContainer component={Paper} elevation={0}
          sx={{ border: `1px solid ${themeColors.border}`, borderRadius: '12px', overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: '12px', color: themeColors.text.secondary, letterSpacing: '0.5px', pl: 2.5 }}>TAG</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '12px', color: themeColors.text.secondary, letterSpacing: '0.5px' }}>COLOUR</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '12px', color: themeColors.text.secondary, letterSpacing: '0.5px' }}>ORGANISATION</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '12px', color: themeColors.text.secondary, letterSpacing: '0.5px' }}>LEADS USING TAG</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '12px', color: themeColors.text.secondary, letterSpacing: '0.5px' }}>CREATED BY</TableCell>
                {(canUpdate || canDelete) && (
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '12px', color: themeColors.text.secondary, letterSpacing: '0.5px', pr: 2.5 }}>ACTIONS</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : tags.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 8 }}>
                    <TagIcon sx={{ fontSize: 48, color: 'rgba(0,0,0,0.12)', mb: 1, display: 'block', mx: 'auto' }} />
                    <Typography fontWeight={600} color="text.secondary">No tags yet</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Create tags to organise and filter your leads
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : tags.map((tag, idx) => (
                <TableRow key={tag.id}
                   sx={{
                     bgcolor: idx % 2 === 0 ? '#fff' : '#fafafa',
                     '&:hover': { bgcolor: '#f2f4f7' },
                   }}>

                  <TableCell sx={{ pl: 2.5, py: 1.5 }}>
                    <Chip
                      label={tag.name}
                      size="small"
                      sx={{
                        bgcolor: `${tag.color}20`, color: tag.color,
                        border: `1px solid ${tag.color}50`,
                        fontWeight: 700, fontSize: '13px', height: 26,
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: tag.color, flexShrink: 0 }} />
                      <Typography fontSize="12px" color="text.secondary">{tag.color}</Typography>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Typography fontSize="12.5px" color="text.secondary">
                      {tag.orgName || 'All Organizations'}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography fontSize="13px" fontWeight={600} color={(tag as any).count?.leads > 0 ? themeColors.primary : 'text.secondary'}>
                        {(tag as any).count?.leads ?? 0}
                      </Typography>
                      <Typography fontSize="12px" color="text.secondary">lead{((tag as any).count?.leads ?? 0) !== 1 ? 's' : ''}</Typography>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Typography fontSize="12.5px" color="text.secondary">
                      {(tag as any).createdBy?.name ?? '—'}
                    </Typography>
                  </TableCell>

                  {(canUpdate || canDelete) && (
                    <TableCell align="right" sx={{ pr: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        {canUpdate && (isSuperAdmin || !!tag.orgId) && (
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => setEditingTag(tag)}
                              sx={{ color: '#9ca3af', '&:hover': { color: themeColors.primary, bgcolor: '#F2EEF9' } }}>
                              <EditIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canDelete && (isSuperAdmin || !!tag.orgId) && (
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => setDeletingTag(tag)}
                              sx={{ color: themeColors.text.muted, '&:hover': { color: '#ef4444', bgcolor: '#fee2e2' } }}>
                              <DeleteIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {isSuperAdmin && (
        <OrgPickerDialog
          open={orgPickerOpen}
          orgs={orgs}
          onClose={() => setOrgPickerOpen(false)}
          onSelect={handleOrgSelected}
        />
      )}

      <TagFormDialog
        open={formOpen}
        tag={null}
        orgName={selectedOrgName ?? undefined}
        onClose={() => {
          setFormOpen(false)
          setSelectedOrgId(null)
          setSelectedOrgName(null)
        }}
        onSave={async (name, color) => { await createMut.mutateAsync({ name, color }) }}
        isSaving={createMut.isPending}
      />

      {editingTag && (
        <TagFormDialog
          open={!!editingTag}
          tag={editingTag}
          onClose={() => setEditingTag(null)}
          onSave={async (name, color) => { await updateMut.mutateAsync({ id: editingTag.id, name, color }) }}
          isSaving={updateMut.isPending}
        />
      )}

      <DeleteDialog
        open={!!deletingTag}
        title="Delete Tag"
        onClose={() => setDeletingTag(null)}
        onConfirm={() => deletingTag && deleteMut.mutate(deletingTag.id)}
        isDeleting={deleteMut.isPending}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Are you sure you want to delete the tag{' '}
          {deletingTag && (
            <Chip
              label={deletingTag.name}
              size="small"
              sx={{ bgcolor: `${deletingTag.color}20`, color: deletingTag.color, fontWeight: 700, mx: 0.5 }}
            />
          )}?
        </Typography>
        {deletingTag && (deletingTag as any).count?.leads > 0 && (
          <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#fff7ed', borderRadius: '8px', border: '1px solid #fed7aa' }}>
            <Typography fontSize="12.5px" color="#c2410c" fontWeight={500}>
              ⚠️ This tag is used on {(deletingTag as any).count.leads} lead(s). Deleting it will remove it from all of them.
            </Typography>
          </Box>
        )}
      </DeleteDialog>
    </Box>
  )
})

export default TagsPage
